
import { useState, useEffect } from "react";
import { AdminSettings, defaultSettings } from "@/types/adminSettings";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logAdminSettings } from "@/utils/consoleLogger";
import { clearConsoleSettingsCache } from "@/utils/consoleLogger";

export const useAdminSettings = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<AdminSettings>(defaultSettings);
  const [isSaving, setIsSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch current admin user and settings
  useEffect(() => {
    fetchCurrentUserAndSettings();
  }, []);

  const fetchCurrentUserAndSettings = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUser(user);
        
        // Fetch settings data
        const { data: settingsData, error } = await supabase
          .from('admin_settings')
          .select('settings, about_us')
          .eq('id', 1)
          .single();
        
        if (settingsData && !error) {
          // Convert from Json to AdminSettings with type assertion
          const fetchedSettings = settingsData.settings as unknown as AdminSettings;
          logAdminSettings("Fetched settings:", JSON.stringify(fetchedSettings));
          
          // Deep merge with defaults to ensure all fields are present
          const mergedSettings = deepMerge(defaultSettings, fetchedSettings);
          
          // Handle the about_us column directly
          if (settingsData.about_us) {
            mergedSettings.aboutUs = settingsData.about_us as unknown as { 
              content: string; 
              lastUpdated: string;
            };
          }
          
          logAdminSettings("Merged settings with defaults:", JSON.stringify(mergedSettings));
          setSettings(mergedSettings);
        } else if (error) {
          logAdminSettings("Error fetching admin settings:", error);
        }
      }
    } catch (error) {
      logAdminSettings("Error in fetchCurrentUserAndSettings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to perform deep merge of objects
  const deepMerge = (target: any, source: any): any => {
    const output = { ...target };
    
    if (isObject(target) && isObject(source)) {
      Object.keys(source).forEach(key => {
        if (isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = deepMerge(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    
    return output;
  };

  // Helper to check if value is an object
  const isObject = (item: any): boolean => {
    return item && typeof item === 'object' && !Array.isArray(item);
  };

  const saveSettings = async () => {
    try {
      setIsSaving(true);
      
      logAdminSettings("Saving settings:", JSON.stringify(settings));
      
      // Extract aboutUs to save in the separate about_us column
      const { aboutUs, ...restSettings } = settings;
      
      const settingsToSave = {
        ...restSettings,
        smtp: {
          ...restSettings.smtp,
          fromName: restSettings.smtp.fromName || 'LamuLamu',
          password: restSettings.smtp.password || '',
          fromEmail: restSettings.smtp.fromEmail || ''
        },
        email: {
          ...restSettings.email,
          admin: restSettings.email.admin || 'info@lamulamu.com',
          test: restSettings.email.test || 'info@lamulamu.com',
          copyToAdmin: restSettings.email.copyToAdmin || false
        },
        whatsapp: {
          ...restSettings.whatsapp,
          enabled: restSettings.whatsapp.enabled || false,
          adminPhone: restSettings.whatsapp.adminPhone || '',
          templates: {
            booking: restSettings.whatsapp.templates.booking || defaultSettings.whatsapp.templates.booking,
            cancellation: restSettings.whatsapp.templates.cancellation || defaultSettings.whatsapp.templates.cancellation,
            review: restSettings.whatsapp.templates.review || defaultSettings.whatsapp.templates.review,
            signup: restSettings.whatsapp.templates.signup || defaultSettings.whatsapp.templates.signup
          }
        }
      };
      
      // Log what we're actually saving to debug
      logAdminSettings("Saving to database:", {
        settings: settingsToSave,
        about_us: aboutUs,
      });
      
      // Update the admin_settings table with both settings and about_us
      const { error } = await supabase
        .from('admin_settings')
        .update({ 
          settings: settingsToSave as unknown as Record<string, any>,
          about_us: aboutUs,
          updated_at: new Date().toISOString()
        })
        .eq('id', 1);

      if (error) {
        logAdminSettings("Error saving settings:", error);
        throw error;
      }

      toast({
        title: "Settings saved",
        description: "Your admin settings have been updated successfully."
      });
      
      clearConsoleSettingsCache();
      // Explicitly refetch settings to ensure we have the latest data
      await fetchCurrentUserAndSettings();
      
    } catch (error: any) {
      logAdminSettings("Error in saveSettings:", error);
      toast({
        variant: "destructive",
        title: "Error saving settings",
        description: error.message
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateSettings = (path: string[], value: any) => {
    setSettings(prevSettings => {
      const newSettings = { ...prevSettings };
      let current: any = newSettings;
      
      // Navigate to the nested object
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) {
          current[path[i]] = {};
        }
        current = current[path[i]];
      }
      
      // Update the value
      current[path[path.length - 1]] = value;
      return newSettings;
    });
  };

  return { settings, isSaving, isLoading, currentUser, saveSettings, updateSettings };
};
